import React, { useCallback, useLayoutEffect } from 'react';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type GadgetMetricsEventType, GADGET_METRICS_TYPE } from '@atlassian/jira-dashboard-common';
import {
	useMessageBus,
	GadgetFallback,
	GADGET_RENDER_CONTAINER_EVENT,
	FALLBACK_GADGET_SOURCE,
} from '@atlassian/jira-dashboard-internal-common';
import { useIntl } from '@atlassian/jira-intl';
import { useSpaStateTransition } from '@atlassian/jira-spa-state-controller';
import messages from './messages';

export const UnconfiguredGadgetFallback = ({
	id,
	hasEditPermissions,
}: {
	id: string;
	hasEditPermissions: boolean;
}) => {
	const { formatMessage } = useIntl();
	const [, { broadcastMessage: broadcastMessageBusListener }] = useMessageBus();
	const [{ currentPageId }] = useSpaStateTransition();

	const reportGadgetMetrics = useCallback(
		(eventType: GadgetMetricsEventType) => {
			broadcastMessageBusListener(
				GADGET_METRICS_TYPE.REPORT,
				{
					eventType,
					contentType: 'Unconfigured',
					source: FALLBACK_GADGET_SOURCE,
				},
				{ gadgetId: id, pageId: currentPageId },
			);
		},
		[broadcastMessageBusListener, id, currentPageId],
	);

	useLayoutEffect(() => {
		reportGadgetMetrics(GADGET_RENDER_CONTAINER_EVENT);
	}, [reportGadgetMetrics]);

	return (
		<GadgetFallback
			icon={
				<SettingsIcon
					label={formatMessage(messages.settingsIconLabel)}
					primaryColor={token('color.icon', colors.N500)}
				/>
			}
			id={id}
		>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.mainMessage)}</p>
			<p>
				{formatMessage(
					hasEditPermissions
						? messages.instructionsWithEditPermissions
						: messages.instructionsWithoutEditPermissions,
				)}
			</p>
		</GadgetFallback>
	);
};
